import { useState, useEffect, useCallback } from 'react';
import { useNotify } from 'react-admin';
import { API_BASE } from '../utils/apiClient';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PastGames } from '../components/PastGames';
import { CompanionEditor } from '../components/CompanionEditor';
import type { CompanionData } from '@garuda/shared';

// ─── Types ──────────────────────────────────────────────

interface Strategy {
  id: string;
  type: 'entity' | 'attribute';
  displayTemplate: string;
  defaultDifficulty: number;
  minGroupSize: number;
  entityType: string;
  supportedCategories?: string[];
}

interface GeneratedTile {
  displayText: string;
  entityId?: string;
}

interface GeneratedGroup {
  strategyId: string;
  label: string;
  difficulty: number;
  tiles: GeneratedTile[];
  warnings?: string[];
}

interface GenerateResult {
  category: string;
  tileType: string;
  groups: GeneratedGroup[];
  overlaps: string[];
  warnings: string[];
}

interface SelectedStrategy {
  strategyId: string;
  difficulty: number;
}

// ─── Constants ──────────────────────────────────────────

const DIFFICULTY_COLORS: Record<number, { label: string; color: string; bg: string }> = {
  1: { label: 'Yellow (Easiest)', color: '#000', bg: '#f9df6d' },
  2: { label: 'Green', color: '#000', bg: '#a0c35a' },
  3: { label: 'Blue', color: '#000', bg: '#b0c4ef' },
  4: { label: 'Purple (Hardest)', color: '#fff', bg: '#ba81c5' },
};

const CATEGORIES = [
  'tamil', 'hindi', 'telugu', 'malayalam', 'kannada',
  'bengali', 'marathi', 'punjabi', 'gujarati', 'odia', 'assamese', 'english',
];

const TILE_TYPES = [
  { id: 'entity', name: 'Entity (Movie Titles)' },
  { id: 'attribute', name: 'Attribute (Cast, Characters)' },
  { id: 'mixed', name: 'Mixed' },
];

// ─── Component ──────────────────────────────────────────

export const ConnectionsCreate = () => {
  const notify = useNotify();
  const [activeTab, setActiveTab] = useState(0);
  const [duplicateWarning, setDuplicateWarning] = useState('');

  // Form state
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [category, setCategory] = useState('tamil');
  const [tileType, setTileType] = useState('entity');
  const [yearStart, setYearStart] = useState('');
  const [yearEnd, setYearEnd] = useState('');
  const [curatedOnly, setCuratedOnly] = useState(true);

  // Strategy state
  const [availableStrategies, setAvailableStrategies] = useState<Strategy[]>([]);
  const [selectedStrategies, setSelectedStrategies] = useState<SelectedStrategy[]>([
    { strategyId: '', difficulty: 1 },
    { strategyId: '', difficulty: 2 },
    { strategyId: '', difficulty: 3 },
    { strategyId: '', difficulty: 4 },
  ]);

  // SEO fields
  const [slug, setSlug] = useState('');
  const [seoDescription, setSeoDescription] = useState('');

  // Companion state
  const [companionData, setCompanionData] = useState<Partial<CompanionData> | null>(null);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<GenerateResult | null>(null);
  const [error, setError] = useState('');
  const [refreshingGroup, setRefreshingGroup] = useState<number | null>(null);
  const [groupValueOverrides, setGroupValueOverrides] = useState<Record<number, string>>({});

  // Load strategies when category changes
  const loadStrategies = useCallback(async (cat: string) => {
    try {
      const res = await fetch(
        `${API_BASE}/admin/api/games/connections/strategies?category=${cat}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed to load strategies');
      const data = await res.json();
      setAvailableStrategies(data.strategies || []);
    } catch (err) {
      notify('Failed to load strategies', { type: 'error' });
      setAvailableStrategies([]);
    }
  }, [notify]);

  useEffect(() => {
    loadStrategies(category);
    // Reset selections when category changes
    setSelectedStrategies([
      { strategyId: '', difficulty: 1 },
      { strategyId: '', difficulty: 2 },
      { strategyId: '', difficulty: 3 },
      { strategyId: '', difficulty: 4 },
    ]);
    setResult(null);
    setError('');
  }, [category, loadStrategies]);

  const handleStrategyChange = (index: number, field: 'strategyId' | 'difficulty', value: string | number) => {
    setSelectedStrategies((prev) => {
      const updated = [...prev];
      if (field === 'strategyId') {
        const strategy = availableStrategies.find((s) => s.id === value);
        updated[index] = {
          ...updated[index],
          strategyId: value as string,
          difficulty: strategy?.defaultDifficulty ?? updated[index].difficulty,
        };
      } else {
        updated[index] = { ...updated[index], difficulty: value as number };
      }
      return updated;
    });
    setResult(null);
  };

  const handleGenerate = async () => {
    const allSelected = selectedStrategies.every((s) => s.strategyId);
    if (!allSelected) {
      notify('Please select all 4 strategies', { type: 'warning' });
      return;
    }

    setGenerating(true);
    setError('');
    setResult(null);

    try {
      const body = {
        category,
        tileType,
        strategies: selectedStrategies.map((s) => ({
          strategyId: s.strategyId,
          difficulty: s.difficulty,
        })),
        constraints: {
          ...(yearStart && yearEnd && !isNaN(parseInt(yearStart)) && !isNaN(parseInt(yearEnd))
            ? { yearRange: [parseInt(yearStart), parseInt(yearEnd)] }
            : {}),
          ...(curatedOnly ? { curatedOnly: true } : {}),
        },
      };

      const res = await fetch(`${API_BASE}/admin/api/games/connections/generate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        let message = 'Generation failed';
        try {
          const data = await res.json();
          message = data.message || data.errors?.join('; ') || message;
        } catch { /* non-JSON response */ }
        setError(message);
        return;
      }

      const data = await res.json();

      setResult(data);

      if (data.warnings?.length > 0) {
        notify(`Generated with ${data.warnings.length} warning(s)`, { type: 'warning' });
      } else {
        notify('Puzzle generated successfully!', { type: 'success' });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!result) return;

    // Check all groups have at least 4 tiles
    const shortGroups = result.groups.filter((g) => g.tiles.length < 4);
    if (shortGroups.length > 0) {
      notify('Some groups have fewer than 4 tiles. Cannot save.', { type: 'error' });
      return;
    }

    setSaving(true);

    try {
      const body: Record<string, unknown> = {
        entityType: 'movie',
        category,
        date,
        tileType,
        groups: result.groups.map((g) => ({
          label: g.label,
          difficulty: g.difficulty,
          tiles: g.tiles.slice(0, 4),
        })),
      };
      if (slug.trim()) body.slug = slug.trim();
      if (seoDescription.trim()) body.seo_description = seoDescription.trim();
      if (companionData?.headline) body.companion = companionData;

      const res = await fetch(`${API_BASE}/admin/api/games/connections`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        let message = 'Failed to save';
        try {
          const data = await res.json();
          message = data.message || message;
        } catch { /* non-JSON response */ }
        throw new Error(message);
      }

      const responseData = await res.json();

      if (responseData.duplicateOf) {
        const msg = `Warning: This puzzle has the same content as ${responseData.duplicateOf.category} on ${responseData.duplicateOf.date}`;
        setDuplicateWarning(msg);
        notify(msg, { type: 'warning' });
      } else {
        notify('Connections game saved!', { type: 'success' });
        window.location.href = '#/game-configs';
      }
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Failed to save', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTile = (groupIndex: number, tileIndex: number) => {
    setResult((prev) => {
      if (!prev) return prev;
      const updatedGroups = prev.groups.map((g, gi) => {
        if (gi !== groupIndex) return g;
        return { ...g, tiles: g.tiles.filter((_, ti) => ti !== tileIndex) };
      });
      // Recalculate overlaps (only consider first 4 tiles per group — those are the ones saved)
      const savedTiles = updatedGroups.flatMap((g) => g.tiles.slice(0, 4).map((t) => t.displayText));
      const counts = new Map<string, number>();
      for (const t of savedTiles) counts.set(t, (counts.get(t) || 0) + 1);
      const overlaps = [...counts.entries()].filter(([, c]) => c > 1).map(([t]) => t);
      return { ...prev, groups: updatedGroups, overlaps };
    });
  };

  const handleRefreshGroup = async (groupIndex: number) => {
    if (!result) return;

    const group = result.groups[groupIndex];
    const strategy = selectedStrategies[groupIndex];
    if (!strategy?.strategyId) return;

    // Collect tiles from all OTHER groups (first 4 each)
    const existingTiles = result.groups
      .filter((_, i) => i !== groupIndex)
      .flatMap((g) => g.tiles.slice(0, 4).map((t) => t.displayText));

    const valueOverride = groupValueOverrides[groupIndex]?.trim();

    setRefreshingGroup(groupIndex);
    try {
      const body = {
        category,
        tileType,
        strategies: [{
          strategyId: strategy.strategyId,
          difficulty: group.difficulty,
          ...(valueOverride ? { params: { value: valueOverride } } : {}),
        }],
        existingTiles,
        constraints: {
          ...(yearStart && yearEnd && !isNaN(parseInt(yearStart)) && !isNaN(parseInt(yearEnd))
            ? { yearRange: [parseInt(yearStart), parseInt(yearEnd)] }
            : {}),
          ...(curatedOnly ? { curatedOnly: true } : {}),
        },
      };

      const res = await fetch(`${API_BASE}/admin/api/games/connections/generate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        let message = 'Refresh failed';
        try {
          const data = await res.json();
          message = data.message || message;
        } catch { /* non-JSON response */ }
        notify(message, { type: 'error' });
        return;
      }

      const data = await res.json();

      if (data.groups.length === 0) {
        notify(data.warnings?.join('; ') || 'No group generated', { type: 'error' });
        return;
      }

      // Replace only the refreshed group
      setResult((prev) => {
        if (!prev) return prev;
        const updatedGroups = prev.groups.map((g, i) =>
          i === groupIndex ? { ...data.groups[0], difficulty: group.difficulty } : g
        );
        // Recalculate overlaps
        const savedTiles = updatedGroups.flatMap((g) => g.tiles.slice(0, 4).map((t: GeneratedTile) => t.displayText));
        const counts = new Map<string, number>();
        for (const t of savedTiles) counts.set(t, (counts.get(t) || 0) + 1);
        const overlaps = [...counts.entries()].filter(([, c]) => c > 1).map(([t]) => t);
        return { ...prev, groups: updatedGroups, overlaps };
      });

      // Clear the override after successful refresh
      setGroupValueOverrides((prev) => {
        const updated = { ...prev };
        delete updated[groupIndex];
        return updated;
      });

      notify('Group refreshed!', { type: 'success' });
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Refresh failed', { type: 'error' });
    } finally {
      setRefreshingGroup(null);
    }
  };

  const canGenerate = selectedStrategies.every((s) => s.strategyId) && !generating;
  const canSave =
    result &&
    result.groups.every((g) => g.tiles.length >= 4) &&
    result.overlaps.length === 0 &&
    !saving;

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => (window.location.href = '#/game-configs')}
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ flex: 1 }}>
          Create Connections Game
        </Typography>
        {activeTab === 0 && (
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!canSave}
          >
            {saving ? 'Saving...' : 'Save Game'}
          </Button>
        )}
      </Box>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="Create" />
        <Tab label="Past Games" />
      </Tabs>

      {activeTab === 1 && <PastGames gameType="connections" category={category} />}

      {activeTab === 0 && (<>

      {/* Duplicate Warning */}
      {duplicateWarning && (
        <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setDuplicateWarning('')}>
          {duplicateWarning}
        </Alert>
      )}

      {/* Config Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Game Settings
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  label="Category"
                >
                  {CATEGORIES.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Tile Type</InputLabel>
                <Select
                  value={tileType}
                  onChange={(e) => setTileType(e.target.value)}
                  label="Tile Type"
                >
                  {TILE_TYPES.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3} sm={1.5}>
              <TextField
                label="Year From"
                type="number"
                value={yearStart}
                onChange={(e) => setYearStart(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={3} sm={1.5}>
              <TextField
                label="Year To"
                type="number"
                value={yearEnd}
                onChange={(e) => setYearEnd(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={curatedOnly}
                    onChange={(e) => setCuratedOnly(e.target.checked)}
                    size="small"
                  />
                }
                label="Curated only"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* SEO Fields */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            SEO (Optional — auto-generated if empty)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                fullWidth
                size="small"
                helperText="URL-friendly identifier (e.g., connections-tamil-vikram-kamal-may-10-2026)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="SEO Description"
                value={seoDescription}
                onChange={(e) => setSeoDescription(e.target.value)}
                fullWidth
                size="small"
                multiline
                minRows={2}
                helperText="Search engine description (100-300 chars)"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Companion Article */}
      <Box sx={{ mb: 3 }}>
        <CompanionEditor
          value={companionData}
          onChange={setCompanionData}
          gameType="connections"
          groups={result?.groups.map((g) => ({ label: g.label, difficulty: g.difficulty })) || []}
        />
      </Box>

      {/* Strategy Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Strategies (4 groups)
          </Typography>
          {availableStrategies.length === 0 ? (
            <Alert severity="info">
              No strategies available for category &quot;{category}&quot;. Try a different category.
            </Alert>
          ) : (
            <>
              {selectedStrategies.map((selected, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 1.5 }}>
                  <Grid item xs={1}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        bgcolor: DIFFICULTY_COLORS[selected.difficulty]?.bg || '#ccc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mt: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 'bold', color: DIFFICULTY_COLORS[selected.difficulty]?.color }}
                      >
                        {index + 1}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={7}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Strategy {index + 1}</InputLabel>
                      <Select
                        value={selected.strategyId}
                        onChange={(e) => handleStrategyChange(index, 'strategyId', e.target.value)}
                        label={`Strategy ${index + 1}`}
                      >
                        {availableStrategies.map((s) => (
                          <MenuItem key={s.id} value={s.id}>
                            {s.displayTemplate} ({s.type})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Difficulty</InputLabel>
                      <Select
                        value={selected.difficulty}
                        onChange={(e) =>
                          handleStrategyChange(index, 'difficulty', e.target.value as number)
                        }
                        label="Difficulty"
                      >
                        {[1, 2, 3, 4].map((d) => (
                          <MenuItem key={d} value={d}>
                            {DIFFICULTY_COLORS[d].label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              ))}

              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={generating ? <CircularProgress size={20} /> : <AutoFixHighIcon />}
                  onClick={handleGenerate}
                  disabled={!canGenerate}
                >
                  {generating ? 'Generating...' : 'Generate Puzzle'}
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Results */}
      {result && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Generated Puzzle
            </Typography>

            {/* Warnings */}
            {result.warnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Warnings:</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                  {result.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </Alert>
            )}

            {/* Overlaps */}
            {result.overlaps.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Overlapping tiles:</strong>{' '}
                {result.overlaps.map((o, i) => (
                  <Chip key={i} label={o} size="small" sx={{ mr: 0.5 }} />
                ))}
              </Alert>
            )}

            {/* Groups */}
            {result.groups.map((group, groupIndex) => {
              const diffColor = DIFFICULTY_COLORS[group.difficulty];
              return (
                <Box key={groupIndex} sx={{ mb: 2 }}>
                  {groupIndex > 0 && <Divider sx={{ mb: 2 }} />}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip
                      label={`Difficulty ${group.difficulty}`}
                      size="small"
                      sx={{
                        bgcolor: diffColor?.bg,
                        color: diffColor?.color,
                        fontWeight: 'bold',
                      }}
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {group.label}
                    </Typography>
                    <Chip
                      label={`${group.tiles.length} tile${group.tiles.length !== 1 ? 's' : ''} (first 4 saved)`}
                      size="small"
                      color={
                        group.tiles.length >= 4
                          ? 'success'
                          : 'error'
                      }
                      sx={{ fontWeight: 'bold' }}
                    />
                    <TextField
                      size="small"
                      placeholder="Override value"
                      value={groupValueOverrides[groupIndex] || ''}
                      onChange={(e) =>
                        setGroupValueOverrides((prev) => ({ ...prev, [groupIndex]: e.target.value }))
                      }
                      sx={{ width: 180, ml: 'auto' }}
                      InputProps={{ sx: { fontSize: '0.8rem' } }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        refreshingGroup === groupIndex
                          ? <CircularProgress size={16} />
                          : <RefreshIcon />
                      }
                      onClick={() => handleRefreshGroup(groupIndex)}
                      disabled={refreshingGroup !== null}
                    >
                      {refreshingGroup === groupIndex ? 'Refreshing...' : 'Refresh'}
                    </Button>
                  </Box>

                  {group.tiles.length === 0 ? (
                    <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>
                      No tiles generated — insufficient data for this strategy
                    </Alert>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {group.tiles.map((tile, tileIndex) => {
                        const isExtra = tileIndex >= 4;
                        return (
                          <Chip
                            key={tileIndex}
                            label={tile.displayText}
                            variant={isExtra ? 'filled' : 'outlined'}
                            color={isExtra ? 'default' : undefined}
                            onDelete={
                              group.tiles.length <= 4
                                ? undefined
                                : () => handleDeleteTile(groupIndex, tileIndex)
                            }
                            sx={{
                              fontSize: '0.9rem',
                              ...(isExtra && { opacity: 0.7 }),
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}

                  {group.warnings && group.warnings.length > 0 && (
                    <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                      ⚠ {group.warnings.join(', ')}
                    </Typography>
                  )}
                </Box>
              );
            })}

            {/* Total tile count */}
            <Divider sx={{ my: 2 }} />
            {(() => {
              const total = result.groups.reduce((sum, g) => sum + g.tiles.length, 0);
              const allGroupsReady = result.groups.every((g) => g.tiles.length >= 4);
              const noOverlaps = result.overlaps.length === 0;
              const ready = allGroupsReady && noOverlaps;
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Saving first 4 per group (16 tiles) — {total} total shown
                  </Typography>
                  {ready ? (
                    <Chip label="Ready to save" size="small" color="success" />
                  ) : (
                    <Chip
                      label={
                        !allGroupsReady
                          ? 'Each group needs at least 4 tiles'
                          : 'Remove overlapping tiles'
                      }
                      size="small"
                      color="warning"
                    />
                  )}
                </Box>
              );
            })()}
          </CardContent>
        </Card>
      )}

      </>)}
    </Box>
  );
};
